import React, { useEffect, useMemo, useState } from 'react';

import { dashboardInvoiceApis } from '@apis/next/invoiceApis';
import changeIconSvg from '@assets/img/icons/change.svg';
import { FormTitle, FormTitleAction } from '@components/admin/FormTitle';
import { Layout } from '@components/admin/Layout';
import { Button } from '@components/Button';
import { ExportCSVDialog } from '@components/ExportCSVDialog';
import { TagSearchInput } from '@components/Forms/TagSearchInput';
import { TagSearchInputMobxZodControl } from '@components/Forms/TagSearchInputStore';
import { PageSizeForm } from '@components/NextForms/PageSizeForm';
import { TextInput } from '@components/NextForms/TextInput';
import { PaginationWithSearchQuery } from '@components/PaginationWithSearchQuery';
import { QueryContainer } from '@components/QueryContainer';
import { Table, TableCellPropsUnion } from '@components/Table/Table';
import { invoiceStrings } from '@data/invoice';
import {
  availableServiceNames,
  routePlanServiceNameStrings,
  routePlanStrings,
} from '@data/routePlan';
import { useSearch } from '@hooks/next/useSearch';
import { useUser } from '@hooks/queries/useUser';
import {
  orderingInvoiceStatusOptions,
  orderingChargePayDayOptions,
  orderingPaymentStatusOptions,
  orderingChargeDateOptions,
  orderingPreferredArrivalDateOptions,
} from '@models/Invoice2';
import { type Invoice } from '@models/next/Invoice';
import { DashboardInvoiceSearchQuery } from '@models/next/Invoice';
import { ServiceName } from '@models/RoutePlan';
import { empty, getDecodeResult } from '@monoid-dev/mobx-zod-form';
import { ReactForm, useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { getFieldLabel } from '@utils/nextForms';
import { url } from '@utils/pages';
import { DEFAULT_PAGE_SIZE, formatDate, formatPrice } from '@utils/types';
import { useChecklist } from '@utils/useChecklist';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { pick } from 'remeda';

const strings = createStrings({
  ja: {
    title: '請求書一覧',
    searchTitle: '絞り込み',
    download: 'ダウンロード',
    id: 'No.',
    invoiceNo: '請求番号',
    d2dNo: 'D2D番号',
    accountNumber: '台帳番号',
    requestDate: '請求日',
    claim: '請求先',
    dueDate: '支払期限',
    claimAmount: '請求金額',
    creationStatus: '作成状況',
    paymentStatus: '支払状況',
    memberSalesperson: '営業担当',
    details: '詳細',
    from: 'から',
    to: 'まで',
    exportCSV: 'Export CSV',
    fromTheNewTime: '時間順 新→古',
    fromTheOldTime: '時間順 古→新',
    sendStatus: '送信済',
    creatingStatus: '作成中',
    all: 'ALL',
    payStatus: '済',
    unPayStatus: '未',
    selectOneItemAtLeast: '少なくとも1つのアイテムを選択してください',
    reset: 'リセット',
    myCases: '私の案件',
    salespersonJapan: 'CS(日本)',
    customerNo: 'お客様の管理番号',
    tagSearchInputPlaceholder: 'D2D番号、請求番号や請求先などの検索条件を入力してください',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  tagSearchInputContainer: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr 30px) 30px repeat(2, 1fr 30px)',
    rowGap: 20,
    columnGap: 8,
    marginTop: '1rem',
    marginBottom: '1rem',
    backgroundColor: theme.palette.grey[50],
    padding: '1rem 1rem',
  },
  paginationContainer: {
    ...common.flexColumn,
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  buttonContainer: {
    ...common.flexInline,
    columnGap: '0.5rem',
  },
  payDate: {
    ...common.exSmallerText,
    ...common.textGray500,
  },
  searchTailLabel: {
    ...common.middleText,
    ...common.textGray700,
    ...common.textAlignCenter,
    paddingTop: 5,
  },
}));

const useSearchQueries = () => {
  const { search } = useSearch(DashboardInvoiceSearchQuery);

  return useMemo(() => {
    return {
      ...search,
      page: search.page ?? 1,
      page_size: search.page_size ?? DEFAULT_PAGE_SIZE,
      total_price_currency: search.total_price_currency ?? 'JPY',
    } satisfies DashboardInvoiceSearchQuery;
  }, [search]);
};

const TotalPriceInput = observer(
  ({ form }: { form: ReactForm<typeof DashboardInvoiceSearchQuery> }) => {
    const classes = {
      ...useCommonStyles(),
      ...useStyles(),
    };

    const { fields } = form.root;

    return (
      <>
        <div>
          <TextInput
            field={fields.total_price_min}
            inputProps={{
              placeholder: `${getFieldLabel(fields.total_price_min)}(${
                fields.total_price_currency.rawInput
              })`,
            }}
          />
        </div>

        <label className={classes.searchTailLabel}>~</label>

        <div>
          <TextInput
            field={fields.total_price_max}
            inputProps={{
              placeholder: `${getFieldLabel(fields.total_price_max)}(${
                fields.total_price_currency.rawInput
              })`,
            }}
          />
        </div>

        <label className={classes.searchTailLabel}>
          <img
            src={changeIconSvg}
            className={classes.baseButton}
            style={{ width: 20 }}
            onClick={() =>
              fields.total_price_currency.setOutput(
                getDecodeResult(fields.total_price_currency.decodeResult) === 'USD' ? 'JPY' : 'USD',
              )
            }
          />
        </label>
      </>
    );
  },
);

const InvoiceListSearchForm = () => {
  const { updateSearch } = useSearch(DashboardInvoiceSearchQuery);

  const queries = useSearchQueries();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const form = useForm(DashboardInvoiceSearchQuery, {
    initialOutput: queries,
    enableReinitialize: true,
  });

  const { fields } = form.root;

  const controller = useMemo(
    () =>
      new TagSearchInputMobxZodControl(
        pick(fields, [
          'd2d_no',
          'company_name',
          'quantity',
          'job_code',
          'invoice_status',
          'payment_status',
          'customer_no',
          'd2d_charge_no',
          'member_salesperson',
          'salesperson_japan',
        ]),
        form,
      ),
    [],
  );

  return (
    <form
      {...form.bindForm({
        onSubmit(d) {
          updateSearch({ ...d, page: 1 });
        },
      })}
      className={classes.tagSearchInputContainer}
    >
      <TagSearchInput
        controller={controller}
        placeholder={strings.tagSearchInputPlaceholder}
        styles={{
          wrapper: {
            gridColumn: '1 / -1',
          },
        }}
      />

      <div>
        <TextInput
          field={fields.charge_pay_day_after}
          inputProps={{ placeholder: getFieldLabel(fields.charge_pay_day_after) }}
        />
      </div>
      <label className={classes.searchTailLabel}>{formStrings.from}</label>

      <div>
        <TextInput
          field={fields.charge_pay_day_before}
          inputProps={{ placeholder: getFieldLabel(fields.charge_pay_day_before) }}
        />
      </div>
      <label className={classes.searchTailLabel}>{formStrings.until}</label>

      <div />

      <div>
        <TextInput
          field={fields.charge_date_after}
          inputProps={{ placeholder: getFieldLabel(fields.charge_date_after) }}
        />
      </div>
      <label className={classes.searchTailLabel}>{formStrings.from}</label>

      <div>
        <TextInput
          field={fields.charge_date_before}
          inputProps={{ placeholder: getFieldLabel(fields.charge_date_before) }}
        />
      </div>
      <label className={classes.searchTailLabel}>{formStrings.until}</label>

      <div>
        <TextInput
          field={fields.preferred_arrival_date_after}
          inputProps={{ placeholder: getFieldLabel(fields.preferred_arrival_date_after) }}
        />
      </div>
      <label className={classes.searchTailLabel}>{formStrings.from}</label>

      <div>
        <TextInput
          field={fields.preferred_arrival_date_before}
          inputProps={{ placeholder: getFieldLabel(fields.preferred_arrival_date_before) }}
        />
      </div>
      <label className={classes.searchTailLabel}>{formStrings.until}</label>

      <div />

      <TotalPriceInput form={form} />

      <div className={classes.buttonContainer}>
        <Button
          color="primary"
          shape="round"
          text={formStrings.filter}
          type="submit"
          size="small"
        />
        <Button
          color="primary"
          shape="round"
          text={formStrings.reset}
          type="submit"
          size="small"
          onClick={() => {
            form.root.setOutput(empty);
            form.root.fields.total_price_currency.setOutput('JPY');
          }}
        />
      </div>
    </form>
  );
};

export const InvoiceList: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { updateSearch } = useSearch(DashboardInvoiceSearchQuery);

  useEffect(() => {
    updateSearch({
      payment_status: '未',
      total_price_currency: 'JPY',
    });
  }, []);

  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const actions: FormTitleAction[] = [
    {
      title: strings.exportCSV,
      action: () => {
        setExportDialogOpen(true);
      },
    },
  ];

  const queries = useSearchQueries();
  const invoices = dashboardInvoiceApis.useGet('/dashboard/invoice', {
    queries: {
      ...queries,
      total_price_currency:
        queries.total_price_min || queries.total_price_max
          ? queries.total_price_currency
          : undefined,
    },
  });

  const checklist = useChecklist(
    [],
    (invoices.data?.results ?? []).map((r) => r.id),
  );

  const user = useUser();

  const checkedItems = useMemo(() => {
    const items = (invoices.data?.results ?? [])
      .filter((item) => checklist.checked(item.id))
      .map((item) => item.id);
    return items;
  }, [invoices.data, checklist.checklist]);

  const headColumns: TableCellPropsUnion[] = [
    {
      label: 'checkbox',
      type: 'checkable',
      checked: checklist.allCheckedAndNotEmpty,
      onChange: checklist.toggleCheckAll,
    },
    {
      label: strings.id,
    },
    {
      label: invoiceStrings.d2d_no,
    },
    {
      label: '',
    },
    {
      label: invoiceStrings.payment_status,
      type: 'selectable',
      options: orderingPaymentStatusOptions,
      onChange: (value: any) => {
        updateSearch({
          page: 1,
          payment_status: value,
        });
      },
    },
    {
      label: invoiceStrings.charge_pay_day,
      type: 'selectable',
      options: orderingChargePayDayOptions,
      onChange: (value: any) => {
        updateSearch({
          page: 1,
          ordering: value,
        });
      },
    },
    {
      label: invoiceStrings.invoice_status,
      type: 'selectable',
      options: orderingInvoiceStatusOptions,
      onChange: (value: any) => {
        updateSearch({
          page: 1,
          invoice_status: value ? value : undefined,
        });
      },
    },
    {
      label: invoiceStrings.job_code,
    },
    {
      label: invoiceStrings.quantity,
    },
    {
      label: invoiceStrings.client_id,
    },
    {
      label: invoiceStrings.d2d_charge_no,
    },
    {
      label: routePlanStrings.serviceName,
      type: 'selectable',
      options: [
        ...availableServiceNames.map((serviceName) => ({
          label: routePlanServiceNameStrings[serviceName],
          value: serviceName,
        })),
        { label: strings.all, value: '' },
      ],
      onChange: (value: any) => {
        updateSearch({
          page: 1,
          service_name: value,
        });
      },
    },
    {
      label: invoiceStrings.preferred_arrival_date,
      type: 'selectable',
      options: orderingPreferredArrivalDateOptions,
      onChange: (value: any) => {
        updateSearch({
          page: 1,
          ordering: value,
        });
      },
    },
    {
      label: invoiceStrings.charge_date,
      type: 'selectable',
      options: orderingChargeDateOptions,
      onChange: (value: any) => {
        updateSearch({
          page: 1,
          ordering: value,
        });
      },
    },
    {
      label: invoiceStrings.total_price,
    },
    {
      label: strings.memberSalesperson,
      type: 'selectable',
      options: [
        {
          value: true,
          label: strings.myCases,
        },
        {
          value: false,
          label: 'ALL',
        },
      ],
      onChange(value: any) {
        updateSearch({
          page: 1,
          member_salesperson_id: (value as boolean) ? user.data?.id : undefined,
        });
      },
    },
    {
      label: strings.salespersonJapan,
      type: 'selectable',
      options: [
        {
          value: true,
          label: strings.myCases,
        },
        {
          value: false,
          label: 'ALL',
        },
      ],
      onChange(value: any) {
        updateSearch({
          page: 1,
          salesperson_japan_id: value ? user.data?.id : undefined,
        });
      },
    },
  ];

  function mapItemToTableRowData(index: number, item: Invoice): TableCellPropsUnion[] {
    const totalPriceText = formatPrice(Number(item.total_price), item.total_price_currency);
    const isOverdue =
      item.payment_status !== '済' &&
      item.charge_pay_day &&
      dayjs(item.charge_pay_day).isBefore(dayjs());

    const isPaid = item.payment_status === '済';

    const renderItem = (children: React.ReactNode) => (
      <span
        className={clsx(
          classes.whitespacePre,
          isOverdue && classes.textErrorImportant,
          isPaid && classes.textGray700,
        )}
      >
        {children}
      </span>
    );

    return [
      {
        label: 'checkbox',
        type: 'checkable',
        checked: checklist.checked(item.id),
        onChange: (value: boolean) => checklist.setChecked(item.id, value),
      },
      {
        label: renderItem(index + 1 + (queries.page - 1) * queries.page_size),
      },
      {
        label: (
          <span
            className={clsx(
              classes.link,
              classes.underline,
              classes.whitespacePre,
              isOverdue && classes.textErrorImportant,
            )}
            onClick={() => {
              window
                .open(
                  `${url('/admin/booking/BookingDetails')}?d2dNo=${
                    item.house_bl_number
                  }&type=current`,
                  '_blank',
                )
                ?.focus();
            }}
          >
            {item.house_bl_number || '-'}
          </span>
        ),
      },
      {
        label: (
          <span
            className={clsx(
              classes.link,
              classes.underline,
              classes.whitespacePre,
              isOverdue && classes.textErrorImportant,
            )}
            onClick={() => {
              window
                .open(
                  `${url('/admin/booking/InvoiceDetail')}?d2dNo=${item.house_bl_number}&id=${
                    item.id
                  }`,
                  '_blank',
                )
                ?.focus();
            }}
          >
            {invoiceStrings.invoice}
          </span>
        ),
      },
      {
        label:
          item.payment_status === '未'
            ? renderItem(<div>{item.payment_status}</div>)
            : renderItem(
                <>
                  <div>{item.payment_status}</div>
                  <div className={classes.payDate}>
                    {invoiceStrings.payment_date} {formatDate(item.payment_date) || '-'}
                  </div>
                </>,
              ),
      },
      {
        label: renderItem(formatDate(item.charge_pay_day) || '-'),
      },
      {
        label: renderItem(item.invoice_status || '-'),
      },
      {
        label: renderItem(item.job_code || '-'),
      },
      {
        label: renderItem(item.quantity || '-'),
      },
      {
        label: renderItem(
          (item.client_name_en.length <= 12
            ? item.client_name_en
            : `${item.client_name_en.slice(0, 12)}...`) || '-',
        ),
      },
      {
        label: renderItem(item.d2d_charge_no || '-'),
      },
      {
        label: renderItem(
          availableServiceNames.includes(item.service_name as ServiceName)
            ? routePlanServiceNameStrings[item.service_name as ServiceName]
            : item.service_name || '-',
        ),
      },
      {
        label: renderItem(formatDate(item.preferred_arrival_date) || '-'),
      },
      {
        label: renderItem(formatDate(item.charge_date) || '-'),
      },
      {
        label: renderItem(totalPriceText),
      },
      {
        label: renderItem(item.member_salesperson_last_name || '-'),
      },
      {
        label: renderItem(item.salesperson_japan_last_name || '-'),
      },
    ];
  }

  const bodyRows: TableCellPropsUnion[][] | undefined = invoices.data?.results.map(
    (result, index) => {
      return mapItemToTableRowData(index, result);
    },
  );

  return (
    <Layout title={strings.title}>
      <ExportCSVDialog
        open={exportDialogOpen}
        onClose={() => {
          setExportDialogOpen(false);
        }}
        subject={invoiceStrings.invoice}
        baseUrl="/dashboard/invoice/"
        query={queries}
        selectedIds={checkedItems}
      />

      <div className={classes.container}>
        <FormTitle title={strings.title} actions={actions} titleSize="large" lineDivider={false} />

        <InvoiceListSearchForm />

        <PageSizeForm />

        <div style={{ height: 16 }} />

        <QueryContainer queries={[user, invoices]}>
          <Table columnFixedUntil={4} fixedWidths={[50, 50, 100, 80]}>
            <Table.Head>
              <Table.Row columns={headColumns} />
            </Table.Head>
            <Table.Body>
              {bodyRows?.map((columns, index) => (
                <Table.Row key={index} columns={columns} />
              ))}
            </Table.Body>
          </Table>
        </QueryContainer>
        <br />
        <div className={classes.paginationContainer}>
          <PaginationWithSearchQuery query={invoices} />
        </div>
      </div>
    </Layout>
  );
};
